class UGCVideoCarousel extends HTMLElement {
  constructor () {
    super()
    this.open = false
    this.embla = null
    this.currentIndex = 0
    this.muted = false
    this._creatingButton = false

    this.container = null
    this.soundButton = null
    this.closeButton = null // Will be created dynamically

    // Bind methods once in constructor
    this.boundHandleOpen = this.handleOpen.bind(this)
    this.boundHandleClose = this.handleClose.bind(this)
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
    this.boundHandlePrevClick = this.handlePrevClick.bind(this)
    this.boundHandleNextClick = this.handleNextClick.bind(this)
    this.boundHandleSoundClick = this.handleSoundClick.bind(this)
    this.boundCloseButtonClickHandler = this.closeButtonClickHandler.bind(this)
  }

  connectedCallback () {
    this.container = this.querySelector('.ugc-video-carousel__container')
    this.soundButton = this.querySelector('.ugc-video-carousel__sound')

    if (!this.container || !this.soundButton) {
      console.error('UGC Video Carousel: Missing required child elements.')
      return
    }

    // Don't create close button until the carousel is opened
    this.setupListeners()
    this.setupEmbla()
  }

  disconnectedCallback () {
    this.removeListeners()
    this.removeCloseButton() // Clean up close button
    if (this.embla) {
      this.embla.destroy()
    }
  }

  createCloseButton () {
    // Remove any existing close button first
    this.removeCloseButton()

    // Create the close button element
    this.closeButton = document.createElement('button')
    this.closeButton.type = 'button'
    this.closeButton.className = 'ugc-video-carousel__close absolute top-2 right-2 md:top-3 md:right-3 z-[999] w-8 h-8 flex items-center justify-center bg-white rounded-full p-1'
    this.closeButton.setAttribute('aria-label', 'Close')

    // Create SVG for the close icon
    this.closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `

    // Append to the carousel inner container
    const inner = this.querySelector('.ugc-video-carousel__inner')
    if (inner) {
      inner.appendChild(this.closeButton)

      // Add click event
      this.closeButton.addEventListener('click', this.boundCloseButtonClickHandler)
    }
  }

  removeCloseButton () {
    if (this.closeButton) {
      // Remove event listener
      this.closeButton.removeEventListener('click', this.boundCloseButtonClickHandler)

      // Remove from DOM
      try {
        if (this.closeButton.parentNode) {
          this.closeButton.parentNode.removeChild(this.closeButton)
        }
      } catch (error) {
        console.warn('Error removing close button:', error)
      }

      this.closeButton = null
    }
  }

  // Separate method for the close button click event
  closeButtonClickHandler () {
    this.handleClose()
  }

  setupListeners () {
    this.container.addEventListener('click', (e) => {
      const target = e.target
      if (!target.closest('.ugc-video-carousel__inner')) {
        this.boundHandleClose()
      }
    })
    window.addEventListener('open:ugc-carousel', this.boundHandleOpen)
    window.addEventListener('close:ugc-carousel', this.boundHandleClose)
    document.addEventListener('keydown', this.boundHandleKeyDown)
    this.soundButton.addEventListener('click', this.boundHandleSoundClick)

    // Add chevron control listeners
    const prevButton = this.querySelector('.ugc-video-carousel__control--prev')
    const nextButton = this.querySelector('.ugc-video-carousel__control--next')

    if (prevButton) {
      prevButton.addEventListener('click', this.boundHandlePrevClick)
    }

    if (nextButton) {
      nextButton.addEventListener('click', this.boundHandleNextClick)
    }
  }

  removeListeners () {
    this.container.removeEventListener('click', (e) => {
      const target = e.target
      if (!target.closest('.ugc-video-carousel__inner')) {
        this.boundHandleClose()
      }
    })
    window.removeEventListener('open:ugc-carousel', this.boundHandleOpen)
    window.removeEventListener('close:ugc-carousel', this.boundHandleClose)
    document.removeEventListener('keydown', this.boundHandleKeyDown)
    this.soundButton.removeEventListener('click', this.boundHandleSoundClick)

    // Remove chevron control listeners
    const prevButton = this.querySelector('.ugc-video-carousel__control--prev')
    const nextButton = this.querySelector('.ugc-video-carousel__control--next')

    if (prevButton) {
      prevButton.removeEventListener('click', this.boundHandlePrevClick)
    }

    if (nextButton) {
      nextButton.removeEventListener('click', this.boundHandleNextClick)
    }
  }

  handlePrevClick () {
    if (this.embla) {
      this.embla.scrollPrev()
    }
  }

  handleNextClick () {
    if (this.embla) {
      this.embla.scrollNext()
    }
  }

  setupEmbla () {
    const carousel = this.querySelector('[carousel]')
    if (!carousel) return

    // If embla is already initialized, destroy it first
    if (this.embla) {
      this.embla.destroy()
    }

    this.embla = EmblaCarousel(carousel, {
      axis: 'y',
      containScroll: 'trimSnaps',
      dragFree: false,
      skipSnaps: false,
      speed: 20,
      loop: true,
      startIndex: this.currentIndex,
      align: 'center',
      slidesToScroll: 1,
      inViewThreshold: 0.7,
    })

    this.embla.on('select', () => {
      this.currentIndex = this.embla.selectedScrollSnap()
      this.updateActiveVideo()
    })

    this.embla.on('settle', () => {
      this.currentIndex = this.embla.selectedScrollSnap()
      this.updateActiveVideo()
    })
  }

  updateActiveVideo () {
    const slides = this.querySelectorAll('.ugc-video-carousel__slide')
    if (!slides.length) return

    if (this.currentIndex < 0 || this.currentIndex >= slides.length) {
      this.currentIndex = 0
    }

    const activeSlide = slides[this.currentIndex]
    if (!activeSlide) return

    // Pause all videos first
    const videos = this.querySelectorAll('video')
    videos.forEach(video => video.pause())

    // Handle active slide media
    const activeVideo = activeSlide.querySelector('video')
    const activeImage = activeSlide.querySelector('img')

    if (activeVideo) {
      if (activeSlide.dataset.videoUrl) {
        const needsSrc = activeVideo.src !== activeSlide.dataset.videoUrl
        if (needsSrc) {
          activeVideo.src = activeSlide.dataset.videoUrl
        }
        activeVideo.muted = this.muted

        const playWhenReady = () => {
          activeVideo.play().catch(() => {})
          this.updateSoundButtonState(activeVideo)
        }

        if (activeVideo.readyState >= 3) {
          playWhenReady()
        } else {
          activeVideo.addEventListener('canplay', playWhenReady, { once: true })
          if (needsSrc) activeVideo.load()
        }
      }
      this.soundButton.style.display = 'flex'
    } else if (activeImage) {
      // Logic for image slides
      this.soundButton.style.display = 'none'
    }

    // Deactivate other slides and pause their videos
    slides.forEach((slide, i) => {
      if (i !== this.currentIndex) {
        slide.removeAttribute('data-active')
        const video = slide.querySelector('video')
        if (video) {
          video.pause()
          video.currentTime = 0
        }
      }
    })

    // Set active state on the current slide
    activeSlide.setAttribute('data-active', 'true')

    this.preloadAdjacentMedia(this.currentIndex)
  }

  preloadAdjacentMedia (index) {
    const slides = this.querySelectorAll('.ugc-video-carousel__slide')
    if (slides.length <= 1) return

    const total = slides.length
    const adjacentIndexes = [
      (index + 1) % total,
      (index - 1 + total) % total,
    ]

    adjacentIndexes.forEach(i => {
      const slide = slides[i]
      if (!slide || slide._preloaded) return
      const video = slide.querySelector('video')
      const videoUrl = slide.dataset.videoUrl
      if (video && videoUrl && !video.src) {
        video.src = videoUrl
        video.load()
        slide._preloaded = true
      }
    })
  }

  updateVideoSize (video) {
    const isMobile = window.innerWidth < 768
    const container = video.closest('.ugc-video-carousel__inner')

    if (isMobile) {
      container.style.height = '100dvh'
      container.style.width = '100vw'
      container.style.maxHeight = 'none'
      container.style.maxWidth = 'none'
      video.style.objectFit = 'cover'
      video.style.width = '100%'
      video.style.height = '100%'
    } else {
      const maxHeight = window.innerHeight * 0.9
      const maxWidth = Math.min(1024, window.innerWidth * 0.9)

      // Calculate dimensions based on 9:16 aspect ratio
      const height = maxHeight
      const width = height * (9 / 16)

      container.style.height = `${height}px`
      container.style.width = `${width}px`
      container.style.maxHeight = `${maxHeight}px`
      container.style.maxWidth = `${maxWidth}px`

      video.style.objectFit = 'contain'
      video.style.width = '100%'
      video.style.height = '100%'
    }
  }

  handleSoundClick () {
    const activeVideo = this.querySelector('.ugc-video-carousel__slide[data-active="true"] video')
    if (!activeVideo) return

    this.muted = !this.muted
    this.soundButton.setAttribute('data-muted', this.muted)
    activeVideo.muted = this.muted
  }

  updateSoundButtonState (video) {
    // Force checking for audio after a delay to ensure audio tracks are properly detected
    setTimeout(() => {
      const hasAudio = video.mozHasAudio ||
        Boolean(video.webkitAudioDecodedByteCount) ||
        Boolean(video.audioTracks && video.audioTracks.length)

      // For iOS Safari, assume there is audio unless proven otherwise
      // as detection sometimes fails on iOS
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      const assumeAudio = isiOS || hasAudio

      if (assumeAudio) {
        this.soundButton.removeAttribute('data-no-sound')
        this.soundButton.setAttribute('data-muted', this.muted)
        this.soundButton.style.display = 'flex'
      } else {
        this.soundButton.setAttribute('data-no-sound', 'true')
        this.soundButton.removeAttribute('data-muted')
        this.soundButton.style.display = 'none'
      }
    }, 500) // Delay to ensure audio detection has time to work
  }

  handleOpen (event) {
    const { index, items } = event.detail

    this.currentIndex = index

    this.populateCarousel(items)

    // Create the close button when the carousel opens
    this.createCloseButton()

    this.preloadTargetMedia(index)
      .then(() => {
        const viewport = this.querySelector('.ugc-video-carousel__viewport')
        const slides = viewport.querySelectorAll('.ugc-video-carousel__slide')

        if (slides.length > index) {
          slides[index].setAttribute('data-active', 'true')
          slides[index].style.position = 'relative'
          slides[index].style.display = 'block'
          slides[index].style.opacity = '1'
          slides[index].style.zIndex = '10'

          this.initializeEmblaWithIndex(index)

          setTimeout(() => {
            this.open = true
            window.dispatchEvent(new CustomEvent('show:page-obscure'))
            window.dispatchEvent(new CustomEvent('prevent:page-scroll'))

            // Force a repaint/reflow in iOS to help with fixed element rendering
            const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
            if (isiOS) {
              // Force hardware acceleration and reflow
              this.style.transform = 'translateZ(0)'
              this.style.willChange = 'transform'

              // Force a layout reflow by using a function
              this.forceReflow()

              // Apply the same to the close button if it exists
              if (this.closeButton) {
                this.closeButton.style.transform = 'translateZ(0)'
                this.closeButton.style.willChange = 'transform'
              }
            }

            setTimeout(() => {
              this.resetSlidesForNormalNavigation(index)
            }, 300)
          }, 100)
        }
      })
      .catch(err => {
        console.error('Error preloading media:', err)
        const viewport = this.querySelector('.ugc-video-carousel__viewport')
        const slides = viewport.querySelectorAll('.ugc-video-carousel__slide')

        if (slides.length > index) {
          slides[index].setAttribute('data-active', 'true')
          slides[index].style.position = 'relative'
          slides[index].style.display = 'block'
          slides[index].style.opacity = '1'
          slides[index].style.zIndex = '10'
        }

        this.initializeEmblaWithIndex(index)

        setTimeout(() => {
          this.open = true
          window.dispatchEvent(new CustomEvent('show:page-obscure'))
          window.dispatchEvent(new CustomEvent('prevent:page-scroll'))

          // Force a repaint/reflow in iOS to help with fixed element rendering
          const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
          if (isiOS) {
            // Force hardware acceleration and reflow
            this.style.transform = 'translateZ(0)'
            this.style.willChange = 'transform'

            // Force a layout reflow by using a function
            this.forceReflow()

            // Apply the same to the close button if it exists
            if (this.closeButton) {
              this.closeButton.style.transform = 'translateZ(0)'
              this.closeButton.style.willChange = 'transform'
            }
          }

          setTimeout(() => {
            this.resetSlidesForNormalNavigation(index)
          }, 300)
        }, 100)
      })
  }

  resetSlidesForNormalNavigation (activeIndex) {
    const slides = this.querySelectorAll('.ugc-video-carousel__slide')
    if (!slides.length) return

    const viewport = this.querySelector('.ugc-video-carousel__viewport')
    if (!viewport) return

    slides.forEach((slide, i) => {
      slide.style.position = ''
      slide.style.top = ''
      slide.style.left = ''
      slide.style.opacity = '1'
      slide.style.display = 'block'
      slide.style.zIndex = ''

      // Set active state only for the active slide
      if (i === activeIndex) {
        slide.setAttribute('data-active', 'true')
      } else {
        slide.removeAttribute('data-active')
      }

      slide.className = 'ugc-video-carousel__slide flex-[0_0_100%] relative'
    })

    viewport.style.position = ''
    viewport.style.transform = ''

    this.reinitializeEmblaForDragging(activeIndex)

    setTimeout(() => {
      this.updateActiveVideo()
    }, 100)
  }

  reinitializeEmblaForDragging (index) {
    if (this.embla) {
      this.embla.destroy()
    }

    const carousel = this.querySelector('[carousel]')
    if (!carousel) return

    this.embla = EmblaCarousel(carousel, {
      axis: 'y',
      draggable: true,
      containScroll: 'trimSnaps',
      dragFree: false,
      skipSnaps: false,
      speed: 20,
      loop: true,
      startIndex: index,
      align: 'center',
      slidesToScroll: 1,
    })

    this.currentIndex = index

    this.embla.on('select', () => {
      const newIndex = this.embla.selectedScrollSnap()
      if (this.currentIndex !== newIndex) {
        this.currentIndex = newIndex
        this.updateActiveVideo()
      }
    })
  }

  initializeEmblaWithIndex (index) {
    if (this.embla) {
      this.embla.destroy()
    }

    const carousel = this.querySelector('[carousel]')
    if (!carousel) return

    const slides = carousel.querySelectorAll('.ugc-video-carousel__slide')

    if (slides.length === 0) return

    if (index < 0 || index >= slides.length) {
      index = 0
    }

    slides.forEach((slide, i) => {
      // Clear active state for all slides initially
      slide.removeAttribute('data-active')

      if (i !== index) {
        slide.style.display = 'none'
        slide.style.position = 'absolute'
        slide.style.opacity = '0'
      }
    })

    const viewport = carousel.querySelector('.ugc-video-carousel__viewport')
    viewport.style.transform = 'none'
    viewport.style.position = 'relative'

    const activeSlide = slides[index]
    activeSlide.style.display = 'block'
    activeSlide.style.position = 'relative'
    activeSlide.style.opacity = '1'

    // Set active state immediately to trigger animation
    activeSlide.setAttribute('data-active', 'true')

    this.embla = EmblaCarousel(carousel, {
      axis: 'y',
      draggable: false,
      speed: 20,
      loop: true,
      startIndex: index,
    })

    this.currentIndex = index

    this.updateActiveVideo()
  }

  handleSlideChange (oldIndex, newIndex) {
    const slides = this.querySelectorAll('.ugc-video-carousel__slide')
    if (!slides.length) return

    const oldSlide = slides[oldIndex]
    if (oldSlide) {
      // Remove active state
      oldSlide.removeAttribute('data-active')

      const oldVideo = oldSlide.querySelector('video')
      if (oldVideo) {
        setTimeout(() => {
          oldVideo.pause()
          oldVideo.currentTime = 0
        }, 300)
      }

      oldSlide.style.opacity = '0'

      setTimeout(() => {
        oldSlide.style.display = 'none'
        oldSlide.style.position = 'absolute'
      }, 300)
    }

    const newSlide = slides[newIndex]
    if (newSlide) {
      newSlide.style.display = 'block'
      newSlide.style.position = 'relative'

      newSlide.getBoundingClientRect()

      setTimeout(() => {
        newSlide.style.opacity = '1'
        // Set active state to trigger animation
        newSlide.setAttribute('data-active', 'true')
        this.updateActiveVideo()
      }, 10)
    }
  }

  playCurrentVideo () {
    this.updateActiveVideo()
  }

  populateCarousel (items) {
    const viewport = this.querySelector('.ugc-video-carousel__viewport')
    if (!viewport) return

    viewport.innerHTML = ''

    viewport.style.transform = 'none'
    viewport.style.position = 'relative'

    items.forEach((item, idx) => {
      const originalIndex = item.originalIndex !== undefined ? item.originalIndex : idx
      const videoUrl = item.videoUrl || ''
      const imageUrl = item.imageUrl || ''
      const isVideo = item.isVideo
      const altText = item.altText || 'User generated content'
      const userHandle = item.user?.handle || ''
      const userHandlePlatform = item.user?.platform || ''
      const userHandleLink = item.user?.handleLink || ''

      const slideElement = document.createElement('div')
      slideElement.className = 'ugc-video-carousel__slide flex-[0_0_100%] relative'
      slideElement.dataset.index = String(idx)
      slideElement.dataset.originalIndex = String(originalIndex)
      slideElement.dataset.videoUrl = videoUrl
      slideElement.dataset.imageUrl = imageUrl

      slideElement.style.display = 'none'
      slideElement.style.position = 'absolute'
      slideElement.style.opacity = '0'
      slideElement.style.transition = 'opacity 0.3s ease'
      slideElement.style.top = '0'
      slideElement.style.left = '0'
      slideElement.style.width = '100%'
      slideElement.style.height = '100%'

      const slideInner = document.createElement('div')
      slideInner.className = 'ugc-video-carousel__slide-inner w-full h-full flex items-center justify-center'

      if (isVideo && videoUrl) {
        const videoElement = document.createElement('video')
        videoElement.className = 'w-full h-full object-contain'
        videoElement.setAttribute('playsinline', '')
        videoElement.setAttribute('loop', '')
        videoElement.setAttribute('muted', '')
        videoElement.setAttribute('preload', 'auto')
        videoElement.dataset.index = String(idx)
        if (imageUrl) {
          videoElement.poster = imageUrl
        }

        videoElement.addEventListener('click', () => {
          if (videoElement.paused) {
            videoElement.play()
          } else {
            videoElement.pause()
          }
        })

        slideInner.appendChild(videoElement)
      } else if (imageUrl) {
        const imageElement = document.createElement('img')
        imageElement.className = 'w-full h-full object-contain md:object-contain'
        imageElement.src = imageUrl
        imageElement.alt = altText
        imageElement.loading = 'lazy'
        slideInner.appendChild(imageElement)
      }

      // Add user handle overlay if handle exists
      if (userHandle) {
        const handleOverlay = document.createElement('div')
        handleOverlay.className = 'ugc-video-carousel__slide-overlay absolute bottom-0 left-0 w-full flex items-end justify-start z-20'

        const handleContainer = document.createElement('div')
        handleContainer.className = 'flex text-white w-full h-fit flex-wrap flex-cols text-left justify-start p-3 md:p-4 overflow-clip'

        if (userHandle) {
          const handleContent = document.createElement('div')
          handleContent.className = 'flex items-center w-full font-semibold self-end'

          if (userHandlePlatform) {
            const platformIcon = document.createElement('span')
            platformIcon.className = 'inline-block h-5 w-5 mr-2'
            platformIcon.innerHTML = this.getPlatformIcon(userHandlePlatform)
            handleContent.appendChild(platformIcon)
          }

          // Create a link if userHandleLink is available
          if (userHandleLink) {
            const handleLink = document.createElement('a')
            handleLink.href = userHandleLink
            handleLink.target = '_blank'
            handleLink.rel = 'noopener noreferrer'
            handleLink.className = 'text-white hover:underline'
            handleLink.textContent = userHandle
            handleContent.appendChild(handleLink)
          } else {
            handleContent.appendChild(document.createTextNode(userHandle))
          }

          handleContainer.appendChild(handleContent)
        }

        handleOverlay.appendChild(handleContainer)
        slideInner.appendChild(handleOverlay)
      }

      slideElement.appendChild(slideInner)
      viewport.appendChild(slideElement)

      slideElement.getBoundingClientRect()
    })
  }

  preloadTargetMedia (index) {
    return new Promise((resolve, reject) => {
      const slides = this.querySelectorAll('.ugc-video-carousel__slide')

      if (!slides || index >= slides.length) {
        return reject(new Error('Invalid index or no slides'))
      }

      const targetSlide = slides[index]
      const videoUrl = targetSlide.dataset.videoUrl
      const imageUrl = targetSlide.dataset.imageUrl

      if (videoUrl) {
        const tempVideo = document.createElement('video')
        tempVideo.style.display = 'none'
        tempVideo.setAttribute('preload', 'auto')
        tempVideo.muted = true

        const cleanup = () => {
          tempVideo.removeEventListener('loadeddata', onLoaded)
          tempVideo.removeEventListener('error', onError)
          if (tempVideo.parentNode) {
            tempVideo.parentNode.removeChild(tempVideo)
          }
        }

        const onLoaded = () => {
          clearTimeout(timeoutId)
          cleanup()
          resolve()
        }

        const onError = (err) => {
          clearTimeout(timeoutId)
          cleanup()
          reject(err)
        }

        tempVideo.addEventListener('loadeddata', onLoaded)
        tempVideo.addEventListener('error', onError)

        const timeoutId = setTimeout(() => {
          cleanup()
          resolve() // Resolve even on timeout
        }, 3000)

        document.body.appendChild(tempVideo)
        tempVideo.src = videoUrl
        tempVideo.load()
      } else if (imageUrl) {
        const img = new Image()
        img.onload = resolve
        img.onerror = reject
        img.src = imageUrl
      } else {
        // No media to load, resolve immediately
        resolve()
      }
    })
  }

  handleClose () {
    if (!this.open) return // Prevent recursive calls

    this.open = false
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    window.dispatchEvent(new CustomEvent('hide:page-obscure'))

    // Stop all videos
    const videos = this.querySelectorAll('video')
    videos.forEach(video => {
      try {
        video.pause()
        video.currentTime = 0
        // Try to release resources
        if (video.src) {
          video.removeAttribute('src')
          video.load()
        }
      } catch (e) {
        console.warn('Error stopping video:', e)
      }
    })

    // Remove the close button
    this.removeCloseButton()

    setTimeout(() => {
      this.clearCarousel()
    }, 250)
  }

  handleKeyDown (event) {
    if (!this.open) return
    if (event.key === 'Escape') {
      this.handleClose()
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      if (this.embla) {
        this.embla.scrollPrev()
      }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      if (this.embla) {
        this.embla.scrollNext()
      }
    }
  }

  clearCarousel () {
    const viewport = this.querySelector('.ugc-video-carousel__viewport')
    if (viewport) {
      // First stop and clear all videos to ensure they stop playing
      const videos = viewport.querySelectorAll('video')
      videos.forEach(video => {
        try {
          video.pause()
          video.currentTime = 0
          video.removeAttribute('src')
          video.load()
        } catch (e) {
          console.warn('Error clearing video:', e)
        }
      })

      // Then clear the HTML
      viewport.innerHTML = ''
    }
  }

  set open (value) {
    this.setAttribute('open', value)
  }

  get open () {
    return this.hasAttribute('open')
  }

  forceReflow () {
    // This method forces a layout reflow by reading a layout property
    // and deliberately doing nothing with it
    // We return the value to satisfy the linter, but don't use it
    return this.offsetHeight
  }

  getPlatformIcon (platform) {
    if (platform === 'instagram') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>`
    }
    if (platform === 'tiktok') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19.321 5.562a5.122 5.122 0 01-.443-.258 6.228 6.228 0 01-1.138-.966c-1.175-1.286-1.376-2.693-1.471-3.33h.006C16.225.924 16.21.82 16.203.687a.24.24 0 00-.219-.228l-3.105-.446.007.723V15.8c0 .28 0 .548-.013.805-.046.83-.502 1.548-1.169 1.967-.382.241-.827.386-1.307.386a3.017 3.017 0 01-2.948-3.031 3.020 3.020 0 013.022-3.012c.334 0 .654.06.95.175V9.389a7.467 7.467 0 00-1.008-.073c-4.09 0-7.414 3.319-7.414 7.409s3.324 7.414 7.414 7.414c4.09 0 7.407-3.324 7.407-7.414 0-.126-.006-.237-.007-.36l-.013-8.813c.83.483 1.807.892 2.852 1.013.677.08 1.37.023 1.8-.008v-3.09c-.372.15-1.767.266-2.944-.368a4.406 4.406 0 01-.187-.118z"/></svg>`
    }
    if (platform === 'youtube') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>`
    }
    return ''
  }
}

if (!customElements.get('ugc-video-carousel')) {
  customElements.define('ugc-video-carousel', UGCVideoCarousel)
}
